import { Fragment, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { AvatarDePersona } from '@/components/ui/avatar-de-persona';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { LOBLevel } from '@/types';
import { LobObserversSection } from './lob-observers-section';
import {
  AVISO_SOPORTE_NO_PUEDE,
  atributosSoporteNoPuede,
  useEnSoporte,
} from '@/features/mini-prixia/soporte-solo-mira';
import { miEmpresaService } from '@/features/mi-empresa/services/mi-empresa-service';
import { useApprovalWorkflowStore } from '../store/approval-workflow-store';
import { useMasterDataStore } from '@/features/master-data/store/master-data-store';
import { approvalWorkflowService } from '../services/approval-workflow-service';
import {
  CONDICIONES,
  ruleTypeLabels,
  condicionesDeRegla,
  describirCondiciones,
  armarPayloadRegla,
} from '../utils/rule-conditions';
import { esReglaGeneral, textoDeReglaGeneral } from '../utils/regla-general';
import {
  Shield,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  DollarSign,
  Building2,
  Users,
  Calculator,
  Percent,
  UserPlus,
  Search,
  AlertTriangle,
  MoreVertical,
} from 'lucide-react';
import type {
  ApprovalRuleType,
  ApprovalRule,
  ApproverUser,
  CargoDisponible,
  AlcanceMargen,
  MetricaDeMargen,
  PasoAprobadorPedido,
} from '../types/approval-workflow.types';
import {
  ALCANCE_MARGEN,
  METRICA_DE_MARGEN,
  SELECTOR_DE_METRICA_DISPONIBLE,
  AVISO_MIDE_CON,
} from '../types/approval-workflow.types';

const ruleTypeIcons: Record<ApprovalRuleType, React.ReactNode> = {
  AMOUNT_THRESHOLD: <DollarSign className="w-4 h-4" />,
  GLOBAL_MARGIN: <Percent className="w-4 h-4" />,
  LOB: <Building2 className="w-4 h-4" />,
  CUSTOMER_SEGMENT: <Users className="w-4 h-4" />,
  PRICING_MODEL: <Calculator className="w-4 h-4" />,
  PRESUPUESTO_CLIENTE: <DollarSign className="w-4 h-4" />,
};

/** Un paso de la cadena mientras se está configurando. */
interface PasoElegido {
  clave: string;
  userId?: string;
  customRoleId?: string;
  order: number;
}

/** Identifica un paso llevando el tipo adentro, para no confundir ids. */
const clavePersona = (id: string) => `persona:${id}`;
const claveCargo = (id: string) => `cargo:${id}`;

/** Minúsculas y sin acentos, para que el buscador no dependa de cómo se tipeó. */
const normalizarTexto = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export function ApprovalWorkflowSettings() {
  const {
    config,
    isLoading,
    fetchConfig,
    updateMode,
    updateAcumular,
    addRule,
    updateRule,
    deleteRule,
    toggleRule,
    setRuleApprovers,
  } = useApprovalWorkflowStore();
  // Soporte sólo mira en Mi empresa: el servidor frena toda escritura de aprobaciones (SOPORTE_SOLO_MIRA).
  const enSoporte = useEnSoporte();

  const { lobs, fetchLOBs } = useMasterDataStore();
  // «Mide con» y los observadores por línea sólo significan algo con el Pricer contratado.
  const { menuItems } = useAuth();
  const tienePricer = (menuItems ?? []).includes('menu:pricer-datec');
  const muestraMetrica = tienePricer && SELECTOR_DE_METRICA_DISPONIBLE;

  const [isSavingMode, setIsSavingMode] = useState(false);
  /** Cotizaciones esperando firma por cada regla: el «afecta a N cotizaciones pendientes» de la maqueta. */
  const [pendientesPorRegla, setPendientesPorRegla] = useState<Record<string, number>>({});
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState<ApprovalRule | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isSubmittingRule, setIsSubmittingRule] = useState(false);

  // Approver assignment
  const [showApproverDialog, setShowApproverDialog] = useState(false);
  const [approverRuleId, setApproverRuleId] = useState<string | null>(null);
  const [approverUsers, setApproverUsers] = useState<ApproverUser[]>([]);
  const [cargos, setCargos] = useState<CargoDisponible[]>([]);
  /**
   * La cadena que se está armando. Cada paso apunta a una persona o a un cargo.
   *
   * Se identifica por `clave` y no por userId porque ahora convive con cargos, y
   * un id de persona y uno de cargo podrían coincidir sin que eso signifique
   * nada. La clave lleva el tipo adentro.
   */
  const [selectedApprovers, setSelectedApprovers] = useState<PasoElegido[]>([]);
  const [isSavingApprovers, setIsSavingApprovers] = useState(false);
  /**
   * Texto del buscador del modal. Filtra cargos y personas a la vez: cuando la
   * empresa tiene muchos usuarios, encontrar a alguien scrolleando es un dolor.
   */
  const [busquedaAprobador, setBusquedaAprobador] = useState('');

  // Form state
  const [ruleName, setRuleName] = useState('');
  /** Condiciones marcadas en el formulario; la regla aplica cuando se cumplen todas. */
  const [condiciones, setCondiciones] = useState<ApprovalRuleType[]>([]);
  const [amountThreshold, setAmountThreshold] = useState('');
  const [marginThreshold, setMarginThreshold] = useState('');
  const [lobId, setLobId] = useState('');
  const [customerSegment, setCustomerSegment] = useState('');
  const [pricingModelId, setPricingModelId] = useState('');
  const [requiredApprovers, setRequiredApprovers] = useState('1');
  const [alcanceMargen, setAlcanceMargen] = useState<AlcanceMargen>('AMBOS');
  const [metrica, setMetrica] = useState<MetricaDeMargen>('MARKUP');
  /** Todos los cargos de la empresa, para ofrecerlos en «Nueva regla» y avisar de los que quedaron vacíos. */
  const [cargosDeLaEmpresa, setCargosDeLaEmpresa] = useState<CargoDisponible[]>([]);
  /*
    Quiénes firman, elegidos en el MISMO formulario de la regla.

    🔴 Antes la cadena se asignaba después, en otro diálogo. Una regla recién
    creada no tenía a nadie, así que "pide más firmas que gente hay" no se podía
    controlar en el momento: el tope era el total de aprobadores de la empresa,
    que casi siempre alcanza. La regla nacía bien y se rompía en el segundo paso
    — o directamente se quedaba sin cadena y nadie se enteraba.
  */
  // Claves `persona:<id>` o `cargo:<id>`, en el orden en que firman.
  const [elegidosEnForm, setElegidosEnForm] = useState<string[]>([]);

  // Fetch pricing models for the select
  const [pricingModels, setPricingModels] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    fetchConfig();
    fetchLOBs();
    /*
      Los aprobadores se cargan al entrar, no recién al abrir el diálogo de
      asignarlos. El formulario de la regla los necesita para dos cosas: para
      poder elegir la cadena ahí mismo, y para saber cuántas firmas puede juntar
      realmente esta regla. Sin la lista, el aviso de "pide más firmas que gente
      hay" no tiene contra qué comparar.
    */
    approvalWorkflowService
      .getApproverUsers()
      .then(setApproverUsers)
      .catch(() => setApproverUsers([]));
    // Los cargos, para poder elegirlos ya en «Nueva regla» (y avisar si uno quedó sin nadie).
    Promise.resolve(approvalWorkflowService.getCargos())
      .then((c) => setCargosDeLaEmpresa(Array.isArray(c) ? c : []))
      .catch(() => setCargosDeLaEmpresa([]));
    Promise.resolve(miEmpresaService.pendientesPorRegla())
      .then((p) => setPendientesPorRegla(p && typeof p === 'object' ? p : {}))
      .catch(() => setPendientesPorRegla({}));
    // Fetch pricing models
    import('@/lib/api').then(({ apiClient }) => {
      apiClient
        .get('/api/pricing-models')
        .then((res: any) => {
          const models = Array.isArray(res) ? res : res.models || res.data || [];
          if (Array.isArray(models)) setPricingModels(models);
        })
        .catch(() => {});
    });
  }, [fetchConfig, fetchLOBs]);

  const handleModeChange = async (alwaysRequire: boolean) => {
    setIsSavingMode(true);
    try {
      await updateMode(alwaysRequire ? 'ALWAYS_REQUIRE_APPROVAL' : 'AUTO_APPROVE_IF_MARGINS_MET');
      toast({ title: 'Modo de aprobación actualizado' });
    } catch {
      toast({ title: 'Error', description: 'No se pudo actualizar', variant: 'destructive' });
    } finally {
      setIsSavingMode(false);
    }
  };

  const handleAcumularChange = async (acumular: boolean) => {
    setIsSavingMode(true);
    try {
      await updateAcumular(acumular);
      toast({
        title: acumular
          ? 'Ahora firman los aprobadores de todas las reglas que apliquen'
          : 'Ahora firma solo el aprobador de la regla más exigente',
      });
    } catch {
      toast({ title: 'Error', description: 'No se pudo actualizar', variant: 'destructive' });
    } finally {
      setIsSavingMode(false);
    }
  };

  const resetForm = () => {
    setRuleName('');
    setCondiciones([]);
    setAmountThreshold('');
    setMarginThreshold('');
    setLobId('');
    setCustomerSegment('');
    setPricingModelId('');
    setRequiredApprovers('1');
    setMetrica('MARKUP');
    setElegidosEnForm([]);
    setEditingRule(null);
  };

  const openEditForm = (rule: ApprovalRule) => {
    setEditingRule(rule);
    setRuleName(rule.name);
    setCondiciones(condicionesDeRegla(rule));
    setAmountThreshold(rule.amountThreshold?.toString() || '');
    setMarginThreshold(rule.marginThreshold?.toString() || '');
    setLobId(rule.lobId || '');
    setCustomerSegment(rule.customerSegment || '');
    setPricingModelId(rule.pricingModelId || '');
    setRequiredApprovers(rule.requiredApprovers.toString());
    setAlcanceMargen(rule.alcanceMargen ?? 'TOTAL');
    setMetrica(rule.metrica ?? 'MARKUP');
    // La cadena que ya tiene, en su orden y con sus cargos, para poder verla y cambiarla acá.
    setElegidosEnForm(
      (rule.approvers ?? [])
        .slice()
        .sort((a, b) => a.stepOrder - b.stepOrder)
        .map((a) =>
          a.customRoleId ? claveCargo(a.customRoleId) : a.userId ? clavePersona(a.userId) : ''
        )
        .filter(Boolean)
    );
    setShowRuleForm(true);
  };

  const alternarCondicion = (c: ApprovalRuleType, marcada: boolean) => {
    setCondiciones((prev) => (marcada ? [...prev, c] : prev.filter((x) => x !== c)));
  };

  /** El input de cada condición; se muestra solo cuando su casilla está marcada. */
  const campoDeCondicion = (c: ApprovalRuleType) => {
    switch (c) {
      case 'AMOUNT_THRESHOLD':
        return (
          <div className="space-y-1">
            <Input
              type="number"
              placeholder="50000"
              value={amountThreshold}
              onChange={(e) => setAmountThreshold(e.target.value)}
              aria-label="Monto umbral (Bs)"
            />
            <p className="text-xs text-muted-foreground">Precio final mayor a este monto (Bs)</p>
          </div>
        );
      case 'PRESUPUESTO_CLIENTE':
        return (
          <p className="text-xs text-muted-foreground">
            Aplica cuando el total del documento (con impuestos y boleta) supera el presupuesto del
            cliente, en USD. Si la cotización no tiene presupuesto, no aplica. Elige abajo quién
            firma.
          </p>
        );
      case 'GLOBAL_MARGIN':
        return (
          <div className="space-y-1">
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              placeholder="25"
              value={marginThreshold}
              onChange={(e) => setMarginThreshold(e.target.value)}
              aria-label="Margen umbral (%)"
            />
            <p className="text-xs text-muted-foreground">
              Margen <strong>operativo</strong> menor a este %. Es el que descuenta garantía,
              comisiones, marketing y obsolescencia — no el bruto que se ve en grande en el
              cotizador.
            </p>

            {muestraMetrica && (
              <div className="pt-2 space-y-1">
                <Label className="text-xs" htmlFor="mide-con">
                  Mide con
                </Label>
                <select
                  id="mide-con"
                  aria-label="Mide con"
                  className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  value={metrica}
                  onChange={(e) => setMetrica(e.target.value as MetricaDeMargen)}
                >
                  {(Object.keys(METRICA_DE_MARGEN) as MetricaDeMargen[]).map((k) => (
                    <option
                      key={k}
                      value={k}
                      disabled={k !== 'MARKUP' && k !== (editingRule?.metrica ?? 'MARKUP')}
                    >
                      {METRICA_DE_MARGEN[k].label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">{METRICA_DE_MARGEN[metrica].ayuda}</p>
                <p
                  className="text-xs text-amber-700 dark:text-amber-400"
                  data-testid="aviso-mide-con"
                >
                  {AVISO_MIDE_CON}
                </p>
              </div>
            )}

            <div className="pt-2 space-y-1">
              <Label className="text-xs">¿A qué le mira el margen?</Label>
              <Select
                value={alcanceMargen}
                onValueChange={(v) => setAlcanceMargen(v as AlcanceMargen)}
              >
                <SelectTrigger aria-label="Alcance del margen">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ALCANCE_MARGEN) as AlcanceMargen[]).map((k) => (
                    <SelectItem key={k} value={k}>
                      {ALCANCE_MARGEN[k].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{ALCANCE_MARGEN[alcanceMargen].ayuda}</p>
            </div>
          </div>
        );
      case 'LOB':
        /*
          Con buscador: la empresa tiene decenas de LOBs y un select común
          obliga a scrollear la lista entera para encontrar uno.
        */
        return (
          <SearchableSelect
            options={lobs.map((l) => ({ value: l.id, label: l.name }))}
            value={lobId}
            onValueChange={setLobId}
            placeholder="Seleccionar LOB..."
            searchPlaceholder="Buscar LOB..."
            emptyMessage="Ningún LOB coincide."
          />
        );
      case 'CUSTOMER_SEGMENT':
        return (
          <Select value={customerSegment} onValueChange={setCustomerSegment}>
            <SelectTrigger aria-label="Segmento de Cliente">
              <SelectValue placeholder="Seleccionar segmento..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
              <SelectItem value="SMB">PyME</SelectItem>
              <SelectItem value="GOBIERNO">Gobierno</SelectItem>
              <SelectItem value="STARTUP">Startup</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'PRICING_MODEL':
        return (
          <SearchableSelect
            options={pricingModels.map((m) => ({ value: m.id, label: m.name }))}
            value={pricingModelId}
            onValueChange={setPricingModelId}
            placeholder="Seleccionar modelo..."
            searchPlaceholder="Buscar modelo..."
            emptyMessage="Ningún modelo coincide."
          />
        );
    }
  };

  /*
    Cuánta gente puede firmar esta regla.

    🔴 Una regla que pide más firmas que personas hay es una cotización muerta:
    cada persona firma una sola vez. Pasó en dev — "regla sgs menor a 20%" pedía
    5 firmas con 2 aprobadores — y dejó tres cotizaciones clavadas, imposibles
    de destrabar desde la pantalla de aprobaciones.

    Una regla sin cadena propia la firman todos los aprobadores de la empresa.
  */
  const yaTieneCadena = (editingRule?.approvers?.length ?? 0) > 0;
  const topeDeFirmas =
    elegidosEnForm.length > 0
      ? elegidosEnForm.length
      : yaTieneCadena
        ? new Set(editingRule!.approvers!.map((a) => a.userId ?? `cargo:${a.customRoleId}`)).size
        : approverUsers.length;
  const firmasImposibles = topeDeFirmas > 0 && Number(requiredApprovers) > topeDeFirmas;

  /** Los cargos que se ofrecen en la cadena: sólo los que conceden aprobar (el servidor rechaza el resto). */
  const cargosParaLaCadena = cargosDeLaEmpresa.filter((c) => c.puedeAprobar !== false);

  const alternarEnForm = (clave: string) =>
    setElegidosEnForm((prev) =>
      prev.includes(clave) ? prev.filter((x) => x !== clave) : [...prev, clave]
    );

  /** De las claves del formulario a los pasos que espera el servidor (persona o cargo, en orden). */
  const pasosDeLaCadena = (claves: string[]): PasoAprobadorPedido[] =>
    claves.map((clave) => {
      const id = clave.slice(clave.indexOf(':') + 1);
      return clave.startsWith('cargo:') ? { customRoleId: id } : { userId: id };
    });

  const etiquetaDeClave = (clave: string): string => {
    const id = clave.slice(clave.indexOf(':') + 1);
    if (clave.startsWith('cargo:')) return cargosDeLaEmpresa.find((c) => c.id === id)?.name ?? '';
    const u = approverUsers.find((x) => x.id === id);
    return u ? `${u.firstName} ${u.lastName}` : '';
  };

  /*
    Los pasos de la cadena que hoy NO podría firmar nadie: un cargo sin personas (o que ya no concede
    aprobar) o una persona que dejó de poder aprobar. Se avisa al editar, que es cuando se arregla sin que
    ninguna cotización esté esperando.
  */
  const pasosSinNadie = elegidosEnForm
    .map((clave) => {
      const id = clave.slice(clave.indexOf(':') + 1);
      if (clave.startsWith('cargo:')) {
        if (cargosDeLaEmpresa.length === 0) return null; // aún no se leyeron: no se asume
        const c = cargosDeLaEmpresa.find((x) => x.id === id);
        if (c && c.userCount > 0 && c.puedeAprobar !== false) return null;
        return (
          c?.name ??
          editingRule?.approvers?.find((a) => a.customRoleId === id)?.customRole?.name ??
          'Cargo'
        );
      }
      if (approverUsers.length === 0 || approverUsers.some((u) => u.id === id)) return null;
      const dePersona = editingRule?.approvers?.find((a) => a.userId === id)?.user;
      return dePersona ? `${dePersona.firstName} ${dePersona.lastName}` : 'Una persona';
    })
    .filter((x): x is string => x !== null);

  const handleSubmitRule = async () => {
    if (!ruleName.trim()) return;
    setIsSubmittingRule(true);

    const armado = armarPayloadRegla({
      name: ruleName,
      condiciones,
      amountThreshold,
      marginThreshold,
      lobId,
      customerSegment,
      pricingModelId,
      requiredApprovers,
    });
    if ('error' in armado) {
      toast({ title: armado.error, variant: 'destructive' });
      setIsSubmittingRule(false);
      return;
    }
    const { payload } = armado;
    // A que le mira el margen. Solo tiene sentido si la regla evalua margen.
    if (condiciones.includes('GLOBAL_MARGIN')) payload.alcanceMargen = alcanceMargen;
    // Con qué se mide: sólo con el Pricer (fuera de él no significa nada) y sólo si la regla evalúa margen.
    if (muestraMetrica && condiciones.includes('GLOBAL_MARGIN')) payload.metrica = metrica;

    try {
      if (editingRule) {
        await updateRule(editingRule.id, payload);
        // Si tocó la cadena en el formulario, se guarda junto con la regla.
        if (elegidosEnForm.length > 0) {
          await setRuleApprovers(editingRule.id, pasosDeLaCadena(elegidosEnForm));
        }
        toast({ title: 'Regla actualizada' });
      } else {
        const creada = await addRule(payload);
        // La cadena se asigna en el mismo paso: una regla no debería nacer sin
        // saber quién la firma.
        if (creada?.id && elegidosEnForm.length > 0) {
          await setRuleApprovers(creada.id, pasosDeLaCadena(elegidosEnForm));
        }
        toast({
          title: 'Regla creada',
          description:
            elegidosEnForm.length > 0
              ? `Firman: ${elegidosEnForm
                  .map((clave) => etiquetaDeClave(clave))
                  .filter(Boolean)
                  .join(' → ')}`
              : 'Sin cadena propia: la firman todos los aprobadores de la empresa.',
        });
      }
      setShowRuleForm(false);
      resetForm();
    } catch {
      toast({ title: 'Error', description: 'No se pudo guardar la regla', variant: 'destructive' });
    } finally {
      setIsSubmittingRule(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!deleteConfirmId) return;
    await deleteRule(deleteConfirmId);
    setDeleteConfirmId(null);
    toast({ title: 'Regla eliminada' });
  };

  const openApproverDialog = async (rule: ApprovalRule) => {
    setApproverRuleId(rule.id);
    // Cargar orden existente de aprobadores
    const existing: PasoElegido[] = (rule.approvers || [])
      .sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
      .map((a, idx) =>
        a.customRoleId
          ? { clave: claveCargo(a.customRoleId), customRoleId: a.customRoleId, order: idx + 1 }
          : { clave: clavePersona(a.userId!), userId: a.userId!, order: idx + 1 }
      );
    setSelectedApprovers(existing);
    setBusquedaAprobador('');
    setShowApproverDialog(true);

    /*
      Personas y cargos se piden en paralelo, y cada uno falla por su cuenta: si
      no se pueden leer los cargos, al menos se puede seguir configurando por
      persona, que es como funcionaba antes.
    */
    const [usuarios, cargosLeidos] = await Promise.allSettled([
      approvalWorkflowService.getApproverUsers(),
      approvalWorkflowService.getCargos(),
    ]);
    setApproverUsers(usuarios.status === 'fulfilled' ? usuarios.value : []);
    // Un cargo que no concede aprobar no firma: ofrecerlo sólo lleva a un rechazo al guardar.
    setCargos(
      cargosLeidos.status === 'fulfilled'
        ? cargosLeidos.value.filter((c) => c.puedeAprobar !== false)
        : []
    );
  };

  /*
    Se busca sin acentos ni mayúsculas para que "mendez" encuentre a "Méndez".
    Cargos por nombre; personas por nombre completo o email.
  */
  const terminoBusqueda = normalizarTexto(busquedaAprobador);
  const cargosFiltrados = terminoBusqueda
    ? cargos.filter((c) => normalizarTexto(c.name).includes(terminoBusqueda))
    : cargos;
  const usuariosFiltrados = terminoBusqueda
    ? approverUsers.filter((u) =>
        normalizarTexto(`${u.firstName} ${u.lastName} ${u.email}`).includes(terminoBusqueda)
      )
    : approverUsers;
  const sinResultados =
    !!terminoBusqueda && cargosFiltrados.length === 0 && usuariosFiltrados.length === 0;

  const handleSaveApprovers = async () => {
    if (!approverRuleId) return;
    setIsSavingApprovers(true);
    try {
      // El orden del arreglo es la secuencia de firmas.
      const pasos = [...selectedApprovers]
        .sort((a, b) => a.order - b.order)
        .map((p) => (p.customRoleId ? { customRoleId: p.customRoleId } : { userId: p.userId! }));
      await setRuleApprovers(approverRuleId, pasos);
      toast({ title: 'Aprobadores asignados en orden secuencial' });
      setShowApproverDialog(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: mensajeDeError(err, 'No se pudieron asignar aprobadores'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingApprovers(false);
    }
  };

  /** Agrega o quita un paso, reordenando lo que queda. */
  const alternarPaso = (paso: Omit<PasoElegido, 'order'>) => {
    setSelectedApprovers((prev) => {
      const existe = prev.find((s) => s.clave === paso.clave);
      if (existe) {
        return prev
          .filter((s) => s.clave !== paso.clave)
          .map((s, idx) => ({ ...s, order: idx + 1 }));
      }
      return [...prev, { ...paso, order: prev.length + 1 }];
    });
  };

  const toggleApproverUser = (userId: string) =>
    alternarPaso({ clave: clavePersona(userId), userId });

  const toggleApproverCargo = (customRoleId: string) =>
    alternarPaso({ clave: claveCargo(customRoleId), customRoleId });

  const moveApprover = (clave: string, direction: 'up' | 'down') => {
    setSelectedApprovers((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((s) => s.clave === clave);
      if (idx < 0) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
      [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
      return sorted.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const isAlwaysRequire = config?.defaultMode === 'ALWAYS_REQUIRE_APPROVAL';

  return (
    <div className="space-y-6">
      {/*
        El modo (artefacto «Mi empresa · Aprobaciones»): dos opciones grandes, una
        elegida. Antes eran dos interruptores que se apagaban entre sí: dos
        llaves para una sola pregunta.
      */}
      <section
        aria-labelledby="titulo-modo-aprobacion"
        className="rounded-tarjeta border bg-card p-5 shadow-sm"
      >
        <h2 id="titulo-modo-aprobacion" className="text-lg font-semibold">
          ¿Cuándo se pide aprobación?
        </h2>
        <p className="text-sm text-muted-foreground">
          Aplica a todas las cotizaciones de la empresa.
        </p>
        <div
          role="radiogroup"
          aria-labelledby="titulo-modo-aprobacion"
          className="mt-4 grid gap-3 md:grid-cols-2"
        >
          {[
            /*
              Los textos de la maqueta de Melanie (25-oct). El primero suma «y el
              margen llega al objetivo» porque es lo que hace el servidor: un
              margen bajo el objetivo del modelo también pide firma, haya regla o no.
            */
            {
              siempre: false,
              titulo: 'Solo si aplica alguna regla',
              detalle:
                'Si ninguna regla de abajo aplica y el margen llega al objetivo, la cotización se aprueba sola.',
            },
            {
              siempre: true,
              titulo: 'Siempre',
              detalle: 'Toda cotización pasa por aprobación, aunque no aplique ninguna regla.',
            },
          ].map((op) => {
            const elegida = op.siempre === isAlwaysRequire;
            return (
              <button
                key={op.titulo}
                type="button"
                role="radio"
                aria-checked={elegida}
                disabled={isSavingMode || enSoporte}
                title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
                onClick={() => {
                  if (!elegida) void handleModeChange(op.siempre);
                }}
                className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors duration-snap ease-snap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${
                  elegida ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/40'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${
                    elegida ? 'border-primary' : 'border-border'
                  }`}
                >
                  {elegida && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                </span>
                <span>
                  <span className="block font-semibold">{op.titulo}</span>
                  <span className="block text-sm text-muted-foreground">{op.detalle}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/*
          🔴 Cuando dos reglas aplican a la vez, el sistema gobierna con una
          sola y descarta la otra. El aprobador de la descartada nunca se
          enteraba de que existió una cotización que le correspondía, y en el
          historial se veía una aprobación normal.

          Las descartadas ahora siempre quedan escritas. Este interruptor
          decide algo distinto: si además tienen que firmar.
        */}
        <div className="mt-5 flex items-center justify-between gap-4 border-t pt-4">
          <div>
            <p id="titulo-firmen-todas" className="font-semibold">
              Si aplican varias reglas, que firmen todas
            </p>
            <p className="text-sm text-muted-foreground">
              Si está apagado, firma solo el aprobador de la regla más exigente.
            </p>
          </div>
          <Switch
            aria-labelledby="titulo-firmen-todas"
            tamano="lg"
            checked={config?.acumularAprobadores ?? false}
            onCheckedChange={(checked) => handleAcumularChange(checked)}
            disabled={isSavingMode || enSoporte}
            title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
          />
        </div>
      </section>

      {/*
        Reglas (maqueta de Melanie, 25-oct): el título con la cuenta, los tres
        pasos de cómo se decide, y una tarjeta por regla en vez de la tabla.
      */}
      <section aria-labelledby="titulo-reglas-aprobacion" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="titulo-reglas-aprobacion" className="text-xl font-semibold">
              Reglas de aprobación
              {(config?.rules?.length ?? 0) > 0 && <span> · {config!.rules.length}</span>}
            </h2>
            <p className="text-sm text-muted-foreground">
              Cada regla dice cuándo aplica y quién firma.
            </p>
          </div>
          <Button
            variant="outline"
            className="rounded-full bg-card px-5"
            {...atributosSoporteNoPuede(enSoporte)}
            onClick={() => {
              resetForm();
              setShowRuleForm(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Agregar regla
          </Button>
        </div>

        <ol className="grid gap-3 md:grid-cols-3" aria-label="Cómo se decide quién firma">
          {[
            {
              titulo: 'Prixia revisa todas las reglas',
              detalle: 'margen, monto, línea, segmento del cliente, modelo…',
            },
            config?.acumularAprobadores
              ? {
                  titulo: 'Cada regla que aplica suma sus firmantes',
                  detalle: 'pueden ser varios, ordenados por niveles',
                }
              : {
                  titulo: 'Firma la regla más exigente',
                  detalle: 'sus firmantes, ordenados por niveles',
                },
            {
              titulo: 'Se aprueba cuando firman todos',
              detalle: 'nivel 1 primero, después nivel 2 y así',
            },
          ].map((paso, i) => (
            <li
              key={paso.titulo}
              className="flex items-center gap-3 rounded-tarjeta border bg-card px-4 py-3.5 shadow-sm"
            >
              <span
                aria-hidden="true"
                className="grid size-8 shrink-0 place-items-center rounded-full bg-degradado-prixia text-sm font-bold text-primary-foreground"
              >
                {i + 1}
              </span>
              <span className="min-w-0">
                <span className="block font-semibold">{paso.titulo}</span>
                <span className="block truncate text-sm text-muted-foreground">{paso.detalle}</span>
              </span>
            </li>
          ))}
        </ol>

        {config?.rules && config.rules.length > 0 ? (
          <ul className="flex flex-col gap-3">
            {config.rules.map((rule) => {
              const pendientes = pendientesPorRegla[rule.id] ?? 0;
              const firmantes = [...(rule.approvers ?? [])].sort(
                (a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0)
              );
              const imposible = firmantes.length > 0 && rule.requiredApprovers > firmantes.length;
              return (
                <li key={rule.id}>
                  <article
                    aria-label={`Regla ${rule.name}`}
                    className={`rounded-tarjeta border bg-card p-5 shadow-sm transition-opacity ${
                      rule.isActive ? '' : 'opacity-70'
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <h3 className="text-lg font-semibold">{rule.name}</h3>
                      {pendientes > 0 && (
                        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                          afecta a {pendientes}{' '}
                          {pendientes === 1 ? 'cotización pendiente' : 'cotizaciones pendientes'}
                        </span>
                      )}
                      {!rule.isActive && (
                        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                          apagada
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-1">
                        <Switch
                          aria-label={`Regla ${rule.name} activa`}
                          tamano="lg"
                          checked={rule.isActive}
                          onCheckedChange={(checked) => toggleRule(rule.id, checked)}
                          {...atributosSoporteNoPuede(enSoporte)}
                          className="mr-2"
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditForm(rule)}
                          disabled={enSoporte}
                          title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : 'Editar regla'}
                          aria-label={`Editar regla ${rule.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={`Más acciones de la regla ${rule.name}`}
                              {...atributosSoporteNoPuede(enSoporte)}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => openApproverDialog(rule)}>
                              <UserPlus className="h-4 w-4" aria-hidden="true" />
                              Quién firma
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => setDeleteConfirmId(rule.id)}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                              Borrar regla
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </span>
                    </div>
                    {/* Pedido 17-oct: que una regla sin línea de negocio no se confunda con las de LOB. */}
                    {esReglaGeneral(rule) && (
                      <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
                        {textoDeReglaGeneral(config.acumularAprobadores === true)}
                      </p>
                    )}

                    <dl className="mt-4 grid gap-3 sm:grid-cols-[5rem_1fr]">
                      <dt className="etiqueta-caps pt-1.5">Si</dt>
                      <dd className="flex flex-col gap-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          {condicionesDeRegla(rule).map((c, i) => (
                            <Fragment key={c}>
                              {i > 0 && <span className="text-xs text-muted-foreground">y</span>}
                              <span
                                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1 text-sm font-semibold ${
                                  c === 'GLOBAL_MARGIN'
                                    ? 'border-alerta/30 bg-alerta-suave text-alerta'
                                    : 'bg-muted/50'
                                }`}
                              >
                                {ruleTypeIcons[c]}
                                {ruleTypeLabels[c]}
                              </span>
                            </Fragment>
                          ))}
                          {/*
                            Las reglas que ya existían quedaron mirando solo el total,
                            para que nada cambiara de un día para el otro. Se marca para
                            que se vea, y se pase a "total y cada línea" a propósito.
                          */}
                          {rule.marginThreshold != null &&
                            (rule.alcanceMargen ?? 'TOTAL') === 'TOTAL' && (
                              <Badge
                                variant="outline"
                                className="gap-1 border-alerta/50 text-alerta"
                                title="Mira el promedio de la cotización. Un ítem regalado puede pasar escondido entre las demás líneas."
                              >
                                <AlertTriangle className="h-3 w-3" />
                                solo mira el total
                              </Badge>
                            )}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {describirCondiciones(rule)}
                        </span>
                      </dd>
                      <dt className="etiqueta-caps pt-1.5">Firman</dt>
                      <dd className="flex flex-wrap items-center gap-2">
                        {firmantes.length > 0 ? (
                          firmantes.map((a, idx) => {
                            const nombre = a.customRole
                              ? a.customRole.name
                              : a.user
                                ? `${a.user.firstName} ${a.user.lastName}`
                                : 'Sin asignar';
                            return (
                              <Fragment key={a.id}>
                                {idx > 0 && (
                                  <span aria-hidden="true" className="text-muted-foreground">
                                    →
                                  </span>
                                )}
                                <span className="inline-flex items-center gap-2 rounded-full border bg-card py-1 pl-1 pr-3 text-sm font-semibold">
                                  <AvatarDePersona
                                    className="h-7 w-7 text-[10px]"
                                    nombre={nombre}
                                    clave={a.userId ?? a.customRoleId ?? a.id}
                                  />
                                  {firmantes.length > 1 && (
                                    <span className="text-xs font-bold text-primary">
                                      Nivel {idx + 1}
                                    </span>
                                  )}
                                  {nombre}
                                </span>
                              </Fragment>
                            );
                          })
                        ) : (
                          <button
                            type="button"
                            className="text-sm font-semibold text-primary hover:underline disabled:pointer-events-none disabled:opacity-60"
                            {...atributosSoporteNoPuede(enSoporte)}
                            onClick={() => openApproverDialog(rule)}
                          >
                            + Asignar quién firma
                          </button>
                        )}
                        {/*
                          En rojo si pide más firmas que gente tiene asignada: esa
                          regla no se puede aprobar nunca.
                        */}
                        <Badge
                          variant={imposible ? 'destructive' : 'secondary'}
                          className="ml-1"
                          title={
                            imposible
                              ? `Pide ${rule.requiredApprovers} firmas y tiene ${firmantes.length} aprobadores. Imposible de aprobar.`
                              : undefined
                          }
                        >
                          {rule.requiredApprovers}{' '}
                          {rule.requiredApprovers === 1 ? 'firma' : 'firmas'}
                        </Badge>
                      </dd>
                    </dl>
                  </article>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="rounded-tarjeta border bg-card px-5 py-10 text-center text-muted-foreground">
            <Shield className="mx-auto mb-2 h-8 w-8 opacity-50" aria-hidden="true" />
            <p>Todavía no hay reglas.</p>
            <p className="mt-1 text-xs">
              Las cotizaciones se aprueban sólo por el margen, según lo elegido arriba.
            </p>
          </div>
        )}
      </section>

      {/* Observadores por línea de producto (sólo con el Pricer) */}
      {tienePricer && (
        <LobObserversSection
          lobs={lobs
            .filter((l) => l.level === LOBLevel.SECTOR)
            .map((l) => ({ id: l.id, name: l.name }))}
        />
      )}

      {/* Dialog: Crear/Editar regla */}
      <Dialog
        open={showRuleForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowRuleForm(false);
            resetForm();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Editar regla' : 'Nueva regla de aprobación'}</DialogTitle>
            <DialogDescription>
              Cuándo una cotización pide firma y quién la firma.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre de la regla *</Label>
              <Input
                placeholder="Ej: Cotizaciones grandes"
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
              />
            </div>

            {/*
              Cada casilla es una condición. Se pueden marcar varias y la regla
              aplica solo cuando se cumplen todas — "margen bajo en Servicios",
              no "margen bajo" o "Servicios". Para un "o" se crean dos reglas.
            */}
            <div className="space-y-2">
              <Label>Condiciones *</Label>
              <p className="text-xs text-muted-foreground">
                Marca una o más. La regla aplica cuando se cumplen todas a la vez.
              </p>
              <div className="space-y-1 rounded-md border p-2">
                {CONDICIONES.filter((c) => c !== 'PRESUPUESTO_CLIENTE' || tienePricer).map((c) => {
                  const marcada = condiciones.includes(c);
                  return (
                    <div key={c} className="rounded p-2 hover:bg-muted/50">
                      <label className="flex cursor-pointer items-center gap-3">
                        <Checkbox
                          checked={marcada}
                          onCheckedChange={(v) => alternarCondicion(c, v === true)}
                          aria-label={ruleTypeLabels[c]}
                        />
                        <span className="flex items-center gap-2 text-sm font-medium">
                          {ruleTypeIcons[c]}
                          {ruleTypeLabels[c]}
                        </span>
                      </label>
                      {marcada && <div className="mt-2 pl-7">{campoDeCondicion(c)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/*
              La cadena se elige acá, en el mismo formulario. Antes había que
              crear la regla y después abrir otro diálogo: una regla podía
              quedarse sin nadie que la firmara sin que nadie se enterara.
            */}
            <div className="space-y-2">
              <Label>¿Quién la firma?</Label>
              {approverUsers.length === 0 && cargosParaLaCadena.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  La empresa todavía no tiene aprobadores cargados.
                </p>
              ) : (
                <div className="max-h-40 overflow-y-auto rounded-md border border-border divide-y">
                  {/*
                    Los cargos van primero: es la forma recomendada de armar la cadena (si esa persona no
                    está, otra con el mismo cargo puede firmar). Antes sólo se ofrecían en «Asignar
                    aprobadores», después de crear la regla.
                  */}
                  {cargosParaLaCadena.map((c) => {
                    const clave = claveCargo(c.id);
                    const puesto = elegidosEnForm.indexOf(clave);
                    return (
                      <label
                        key={clave}
                        className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/40"
                      >
                        <input
                          type="checkbox"
                          aria-label={`Firma el cargo ${c.name}`}
                          checked={puesto >= 0}
                          onChange={() => alternarEnForm(clave)}
                        />
                        {puesto >= 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                            {puesto + 1}
                          </span>
                        )}
                        <span>{c.name}</span>
                        <span
                          className={`text-xs ${c.userCount === 0 ? 'font-medium text-destructive' : 'text-muted-foreground'}`}
                        >
                          {c.userCount === 0
                            ? 'cargo · sin nadie asignado'
                            : `cargo · ${c.userCount} ${c.userCount === 1 ? 'persona' : 'personas'}`}
                        </span>
                      </label>
                    );
                  })}
                  {approverUsers.map((u) => {
                    const puesto = elegidosEnForm.indexOf(clavePersona(u.id));
                    return (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/40"
                      >
                        <input
                          type="checkbox"
                          aria-label={`Firma ${u.firstName} ${u.lastName}`}
                          checked={puesto >= 0}
                          onChange={() => alternarEnForm(clavePersona(u.id))}
                        />
                        {puesto >= 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                            {puesto + 1}
                          </span>
                        )}
                        <span>
                          {u.firstName} {u.lastName}
                        </span>
                        <span className="text-xs text-muted-foreground">{u.email}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                El orden en que los marcas es el orden en que firman. Si no eliges a nadie, la
                firman todos los aprobadores de la empresa.
              </p>
              {pasosSinNadie.length > 0 && (
                <p className="text-xs font-medium text-destructive">
                  Nadie con este cargo puede firmar: {pasosSinNadie.join(', ')}. Una cotización que
                  llegue a ese paso se queda parada hasta que alguien lo tenga o lo cambies.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="aprobadores-requeridos">Aprobadores requeridos</Label>
              <Input
                id="aprobadores-requeridos"
                type="number"
                min="1"
                max="10"
                value={requiredApprovers}
                onChange={(e) => setRequiredApprovers(e.target.value)}
              />
              {firmasImposibles ? (
                <p className="text-xs text-destructive">
                  Pediste {requiredApprovers} firmas pero esta regla la pueden firmar{' '}
                  {topeDeFirmas === 1 ? 'una sola persona' : `${topeDeFirmas} personas`}. Cada
                  persona firma una sola vez, así que una cotización con esta regla no se podría
                  aprobar nunca.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {pasosSinNadie.length > 0 && pasosSinNadie.length >= topeDeFirmas ? (
                    'Todavía no hay nadie que pueda firmar esta regla'
                  ) : (
                    <>
                      {requiredApprovers} de {topeDeFirmas}{' '}
                      {topeDeFirmas === 1 ? 'persona disponible' : 'personas disponibles'}
                    </>
                  )}
                  {editingRule && (editingRule.approvers?.length ?? 0) === 0
                    ? ' (todavía sin cadena propia: la firman todos los aprobadores de la empresa)'
                    : ''}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRuleForm(false);
                resetForm();
              }}
              disabled={isSubmittingRule}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitRule}
              disabled={isSubmittingRule || !ruleName.trim() || firmasImposibles}
            >
              {isSubmittingRule ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : editingRule ? (
                'Actualizar'
              ) : (
                'Crear regla'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => !open && setDeleteConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar la regla?</AlertDialogTitle>
            <AlertDialogDescription>
              Las cotizaciones nuevas ya no la tendrán en cuenta. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteRule}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Asignar aprobadores a regla */}
      <Dialog
        open={showApproverDialog}
        onOpenChange={(open) => !open && setShowApproverDialog(false)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Quién firma, en orden</DialogTitle>
            <DialogDescription>
              Define quién firma y en qué orden. Las aprobaciones se piden en secuencia: el paso 2
              no puede firmar hasta que el paso 1 lo haya hecho. Conviene armarla por cargo — así,
              si esa persona no está, otra con el mismo cargo puede firmar y la cotización no se
              queda trabada.
            </DialogDescription>
          </DialogHeader>

          {/* Orden actual */}
          {selectedApprovers.length > 0 && (
            <div className="border rounded-lg p-3 bg-muted/30">
              <p className="text-xs font-medium text-muted-foreground mb-2">Orden de aprobación:</p>
              <div className="space-y-1">
                {[...selectedApprovers]
                  .sort((a, b) => a.order - b.order)
                  .map((s) => {
                    const user = approverUsers.find((u) => u.id === s.userId);
                    const cargo = cargos.find((c) => c.id === s.customRoleId);
                    const etiqueta = cargo
                      ? cargo.name
                      : user
                        ? `${user.firstName} ${user.lastName}`
                        : (s.userId ?? s.customRoleId ?? '');
                    return (
                      <div key={s.clave} className="flex items-center gap-2 text-sm">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                          {s.order}
                        </span>
                        <span className="flex-1 font-medium">
                          {etiqueta}
                          {cargo && (
                            <span className="ml-2 text-xs font-normal text-muted-foreground">
                              (cargo · {cargo.userCount}{' '}
                              {cargo.userCount === 1 ? 'persona' : 'personas'})
                            </span>
                          )}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            disabled={s.order === 1}
                            onClick={() => moveApprover(s.clave, 'up')}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            disabled={s.order === selectedApprovers.length}
                            onClick={() => moveApprover(s.clave, 'down')}
                          >
                            ↓
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={busquedaAprobador}
              onChange={(e) => setBusquedaAprobador(e.target.value)}
              placeholder="Buscar persona o cargo..."
              aria-label="Buscar persona o cargo"
              className="pl-9"
            />
          </div>

          {sinResultados && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Sin resultados para &ldquo;{busquedaAprobador.trim()}&rdquo;
            </p>
          )}

          {/*
            Los cargos van PRIMERO y no al final, a propósito: es la forma
            recomendada de armar la cadena. Elegir personas nominales es lo que
            deja la cotización trabada cuando esa persona no está, así que la
            opción buena tiene que ser la que se ve antes.
          */}
          {cargosFiltrados.length > 0 && (
            <div className="space-y-2 py-2">
              <p className="text-xs font-medium text-muted-foreground">
                Por cargo — lo firma cualquiera que lo tenga (recomendado)
              </p>
              <div className="max-h-40 space-y-2 overflow-y-auto">
                {cargosFiltrados.map((c) => {
                  const elegido = selectedApprovers.find((s) => s.customRoleId === c.id);
                  const vacio = c.userCount === 0;
                  return (
                    <div
                      key={c.id}
                      className={`flex cursor-pointer items-center gap-3 rounded p-2 hover:bg-muted/50 ${elegido ? 'border border-primary/20 bg-primary/5' : ''}`}
                      onClick={() => toggleApproverCargo(c.id)}
                    >
                      <Checkbox checked={!!elegido} aria-label={`Seleccionar cargo ${c.name}`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{c.name}</p>
                        {/*
                          Un cargo sin nadie asignado traba la cadena: la
                          cotización llega a ese paso y no hay quién firme. Se
                          avisa acá, al configurar, que es cuando se puede
                          arreglar sin que nadie esté esperando.
                        */}
                        <p
                          className={`text-xs ${vacio ? 'font-medium text-destructive' : 'text-muted-foreground'}`}
                        >
                          {vacio
                            ? 'Sin nadie asignado — trabaría la cadena en este paso'
                            : `${c.userCount} ${c.userCount === 1 ? 'persona' : 'personas'}`}
                        </p>
                      </div>
                      {elegido && (
                        <Badge variant="default" className="text-xs">
                          Paso {elegido.order}
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Lista de usuarios para seleccionar */}
          {!sinResultados && (
            <>
              <p className="text-xs font-medium text-muted-foreground">
                Por persona — solo esa persona puede firmar
              </p>
              <div className="space-y-2 py-2 max-h-48 overflow-y-auto">
                {approverUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay personas con permiso para aprobar cotizaciones
                  </p>
                ) : usuariosFiltrados.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Ninguna persona coincide con la búsqueda
                  </p>
                ) : (
                  usuariosFiltrados.map((u) => {
                    const selected = selectedApprovers.find((s) => s.userId === u.id);
                    return (
                      <div
                        key={u.id}
                        className={`flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer ${selected ? 'bg-primary/5 border border-primary/20' : ''}`}
                        onClick={() => toggleApproverUser(u.id)}
                      >
                        <Checkbox
                          checked={!!selected}
                          aria-label={`Seleccionar ${u.firstName} ${u.lastName}`}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {u.firstName} {u.lastName}
                          </p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                        {selected && (
                          <Badge variant="default" className="text-xs">
                            Paso {selected.order}
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {u.roleName ??
                            (u.role === 'COMPANY_ADMIN'
                              ? 'Admin'
                              : u.role === 'APPROVER'
                                ? 'Aprobador'
                                : u.role)}
                        </Badge>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowApproverDialog(false)}
              disabled={isSavingApprovers}
            >
              Cancelar
            </Button>
            <Button onClick={handleSaveApprovers} disabled={isSavingApprovers}>
              {isSavingApprovers ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                `Guardar (${selectedApprovers.length})`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
